import React, { useEffect, useState } from 'react'
import CalendarComponent from './CalendarComponent';
import { connect, useSelector } from 'react-redux';
import { updateCell } from '../../actions/orderActions';
import { AvailabilityMode, Cell, Order, PointOfSale, UnavailableWindow } from '@m4a3/api-client';
import { LandingPad } from '../../models/landing-pad.interface';
import { RootState } from '../../store/configureStore';
import UnavailableBlockModal from './UnavailableBlockModal';
import { DateTime } from 'luxon';
import { getAllUnavailableWindows } from '../../actions/unavailableWindowsActions';
import { getEarliestOpeningTime, getLatestClosingTime } from '../../utils/openingHourUtils';
import { getPointOfSales } from '../../actions/adminActions';
import { getActiveCraft } from '../../selectors/liveAircraftSelectors';



interface CellAvailabilityOverviewProps {
  readonly pointOfSales: PointOfSale[];
  readonly getPointofSales: (cellId: number) => void;
  readonly ordersToFilter: Order[];
  readonly mergedOrders: Order[];
  readonly cells: Cell[];
  readonly selectedCellId: number;
  readonly liveData: any;
  readonly pads: LandingPad[];
  readonly onSelectOrder: (orderId: string) => void;
  readonly getAllUnavailableWindows: (cellId: number, startDate?: string, endDate?: string) => void;
  readonly unavailableWindows: UnavailableWindow[];
  readonly updateCell: (newCellData: Cell) => Promise<void>
}

function CellAvailabilityOverview({
  pointOfSales,
  getPointofSales,
  ordersToFilter,
  mergedOrders,
  cells,
  selectedCellId,
  liveData,
  unavailableWindows,
  pads,
  onSelectOrder,
  getAllUnavailableWindows,
  updateCell
}: CellAvailabilityOverviewProps) {
  const [shouldShowCreateUnavailableBlock, setShouldShowCreateUnavailableBlock] = useState<boolean>(false);
  const [editUnavailableBlock, setEditUnavailableBlock] = useState<UnavailableWindow>(null);
  const [availabilityMode, setAvailabilityMode] = useState<AvailabilityMode>();
  const [activePointOfSales, setActivePointOfSales] = useState<PointOfSale[]>([]);
  
  const onlineDrones = useSelector(getActiveCraft);

  const getAndSetAllUnavailableWindows = async () => {
    const now = DateTime.now().setZone(getCellTimezone());

    const startOfDay = now.startOf('day').toJSDate().toISOString();
    const endOfDay = now.endOf('day').toJSDate().toISOString();
    getAllUnavailableWindows(selectedCellId, startOfDay, endOfDay);
  };

  useEffect(() => {
    getAndSetAllUnavailableWindows()
  }, [])

  useEffect(() => {
    const matchingCell = getCurrentCell();
    setAvailabilityMode(matchingCell?.availabilityMode);
    getPointofSales(matchingCell?.id);
  }, [cells])

  useEffect(() => {
    setActivePointOfSales(pointOfSales);
  }, [pointOfSales])

  const getCurrentCell = () => cells?.find(cell => cell.id === selectedCellId);

  const getActivePads= (): LandingPad[] => {
    return pads.filter(pad => 
      pad.active === true && 
      pad.cellId === selectedCellId && 
      pad.aircraft !== 0
    );
  }

  const getDronesWithoutAssignedPad = (): string[] => {
    const activePads = getActivePads();
    const assignedAircraftNumbers = activePads.map(pad => pad.aircraft.toString());
    
    return onlineDrones?.filter(droneName => {
      const aircraftNumber = droneName.replace('MNA-', '');
      return !assignedAircraftNumbers.includes(aircraftNumber) && aircraftNumber !== '0';
    }) || [];
  }

  const shouldShowEditUnavailableBlockModal = () => {
    return !!editUnavailableBlock;
  }
  const getCellTimezone = (): string | undefined => {
    return cells?.find(cell => cell.id === selectedCellId)?.timezone;
  }

  const showCreateUnavailableBlockModalIfRequired = (): JSX.Element => {
    if (shouldShowCreateUnavailableBlock) {
      return <UnavailableBlockModal
        onClose={() => {
          setShouldShowCreateUnavailableBlock(false)
          getAndSetAllUnavailableWindows();
        }}
        cellId={selectedCellId}
        timetablePads={getActivePads()}
        timezone={getCellTimezone()} />
    }
    return <></>;
  }

  const showEditUnavailableBlockModalIfRequired = (): JSX.Element => {
    if (shouldShowEditUnavailableBlockModal()) {
      return <UnavailableBlockModal
        onClose={() => {
          setEditUnavailableBlock(null);
          getAndSetAllUnavailableWindows();
        }}
        cellId={selectedCellId}
        timetablePads={getActivePads()}
        timezone={getCellTimezone()}
        unavailableWindow={editUnavailableBlock} />
    }
    return <></>;
  }

  return (<div style={{height: '100%', position: 'relative', width: '100%'}}>
    <div style={{
      width: '100%',
      height: '100%',
      overflow: 'auto',
      position: 'absolute',
      paddingBottom: '50px'
    }}>
      {showCreateUnavailableBlockModalIfRequired()}
      {showEditUnavailableBlockModalIfRequired()}
      <header className="flex flex-none items-center justify-between border-b border-gray-200 px-6 py-4">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">Timetable</h1>
        </div>
        <div className="flex items-center">
          <div className="hidden md:ml-4 md:flex md:items-center">
            <button
              type="button"
              className="flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={() => {
                setShouldShowCreateUnavailableBlock(true);
              }}
            >
              Add block
            </button>
          </div>
        </div>
      </header>

      <CalendarComponent
        timetablePads={getActivePads()}
        dronesWithoutAssignedPad={getDronesWithoutAssignedPad()}
        orders={ordersToFilter}
        onSelectOrder={(order: Order) => onSelectOrder(order.id)}
        onSelectEditUnavailableBlockType={(block: UnavailableWindow) => setEditUnavailableBlock(block)}
        unavailableWindows={unavailableWindows}
        earliestOpeningTime={getEarliestOpeningTime(activePointOfSales, getCellTimezone())}
        latestClosingTime={getLatestClosingTime(activePointOfSales, getCellTimezone())}
        cell={getCurrentCell()} />

    </div>
  </div>)
};

const mapStateToProps = (state: RootState) => ({
  ordersToFilter: state.orders.ordersToFilter,
  mergedOrders: state.orders.mergedOrders,
  cells: state.orders.cells,
  selectedCellId: state.orders.selectedCellId,
  liveData: state.liveAircraft.liveData,
  pads: state.liveAircraft.pads,
  unavailableWindows: state.unavailableWindows.unavailablewindows,
  pointOfSales: state.admin.pointOfSales,
});

const mapDispatchToProps = dispatch => ({
  getPointofSales: (cellId: number) => dispatch(getPointOfSales(cellId)),
  getAllUnavailableWindows: (cellId: number, startDate?: string, endDate?: string) => dispatch(getAllUnavailableWindows(cellId, startDate, endDate)),
  updateCell: (newCellData: Cell) => dispatch(updateCell(newCellData))
});

export default connect(mapStateToProps, mapDispatchToProps)(CellAvailabilityOverview);
